import React, { useState, useRef, useEffect, useMemo } from 'react';
import TextInput from '../TextInput/TextInput';
import Icon from '../Icon2/Icon';
import Tooltip from '../Tooltip/Tooltip';
import './VersionTimeline.css';
import DateLabel from '../DateLabel/DateLabel';
import Pill from '../Pills/Pill';
import cbModal, { ModalHeader, ModalBody, ModalFooter } from '../Modal/Modal';
import Button from '../Button/Button';
import Search from '../Search/Search';
import EmptyState from '../EmptyState/EmptyState';
import { Truncate } from '../Truncate/Truncate';
import {
  getEnvColor,
  getEnvName,
  getPillText,
  getVersionDisplayName,
  handleVersionNameChange,
  handleVersionNameKeyDown,
  handleVersionClickKeyDown,
  handleEditModeKeyDown,
  handleCompareClick,
  handleCompareKeyDown,
  getLocaleName,
  IEnvironment,
  IVersion,
  BUTTON_ATTRIBUTES,
  MouseEvent,
  sortEnvironmentsByTime
} from './utils';
import Notification from '../Notification/Notification';

export interface IVersionTimeline {
  version: IVersion;
  latestVersion: IVersion;
  defaultSelected?: IVersion;
  onVersionNameChange?: Function;
  versionOnClick?: Function;
  compareVersion?: Function;
  testId?: string;
  versionTestId?: string;
  dateTime?: any;
  userName?: string;
  environments?: Array<IEnvironment>;
  environmentsList?: Array<{ uid: string; name: string; color: string }>;
  locales?: Array<{ code: string; name: string }>;
}

const VersionTimelineDateTime: React.FC<{
  dateTime?: any;
  iconType?: 'clock' | 'publish';
}> = ({dateTime, iconType = 'clock'}) => {
  return (
    <div className="version-timeline__datetime flex-v-center" data-test-id="cs-version-timeline-datetime">
      <Icon icon={iconType === 'publish' ? 'PublishIcon' : 'Clock'} version="v2" size="small" />
      <DateLabel date={dateTime} className='ml-8' type="medium" />
    </div>
  );
};

const VersionTimelineUser: React.FC<{
  userName?: string;}> = ({userName}) => {
  return (
    <div className="version-timeline__user flex-v-center" data-test-id="cs-version-timeline-user">
      <Icon icon="Profile" version="v2" size="small" />
      <span className="ml-8">
        <Truncate 
          isResponsive 
          truncateFrom="end"
          testId="cs-version-timeline-user-name"
        >
          {userName}
        </Truncate>
      </span>
    </div>
  );
};

const PublishStatusPill = ({ text }: { text: string }) => (
  <Pill 
    shouldHaveBorder={false} 
    isSortable={false} 
    size="small"
    variant="chip" 
    status="default" 
    className="ml-8"
    items={[{ id: 1, text }]} 
    testId={`cs-version-timeline-${text}-pill`}
  />
);

export const EnvironmentHeader: React.FC<{
  env: IEnvironment;
  environmentsList: Array<{ uid: string; name: string; color: string }>;
  showPill?: boolean;
  className?: string;
}> = ({ env, environmentsList, showPill, className }) => {
  const pillText = getPillText(env)
  const envColor = getEnvColor(env.environment, environmentsList)
  const envName = getEnvName(env.environment, environmentsList)

  return (
    <div className={`version-timeline__environment flex-v-center`}>
      <Icon icon="EnvironmentColor" fill={envColor} className='ml-5' size='mini' />
      <span className="version-timeline__environment-name ml-12">
        <Truncate 
          isResponsive 
          truncateFrom="end"
          testId="cs-version-timeline-environment-name"
        >
          {envName}
        </Truncate>
      </span>
      {showPill && pillText && <PublishStatusPill text={pillText} />}
    </div>
  );
};

export const EnvironmentContent: React.FC<{
  env: IEnvironment;
  environmentsList: Array<{ uid: string; name: string; color: string }>;
  environments: IVersionTimeline['environments'];
  showPill?: boolean;
}> = ({ env, environmentsList, environments, showPill }) => {
  return (
    <div className="version-timeline__environment-container">
      <EnvironmentHeader
        env={env}
        environmentsList={environmentsList}
        showPill={showPill}
      />
      <VersionTimelineDateTime dateTime={env.scheduled_at ? env.scheduled_at : env.time} iconType="publish"/>
      <VersionTimelineUser userName={env.user}/>
    </div>
  )
}

const EnvironmentItem: React.FC<{
  env: IEnvironment;
  environmentsList: Array<{ uid: string; name: string; color: string }>;
  environments: IVersionTimeline['environments'];
  isModal?: boolean;
}> = ({ env, environmentsList, environments, isModal }) => {
  const isFirstEnvironment = environments[0]?.environment === env.environment
  const pillText = getPillText(env)

  const TooltipDom = () => {
    return (
      <div className="version-timeline__tooltip-content">
        <EnvironmentContent
          env={env}
          environmentsList={environmentsList}
          environments={environments}
          showPill={true}
        />
      </div>
    )
  }

  return (
    <div className="flex-v-center" data-test-id="cs-version-timeline-environment">
      <EnvironmentHeader
        env={env}
        environmentsList={environmentsList}
        showPill={isModal}
      />
      {!isModal && isFirstEnvironment && pillText && (
        <Tooltip content={<TooltipDom/>} position="left" variantType="light" showArrow={false}>
          <Icon icon="Information" version="v2" size="mini" className='ml-3'/>
        </Tooltip>
      )}
    </div>
  );
};

export const VersionTimelineEnvironments: React.FC<{
  environments: IVersionTimeline['environments'];
  environmentsList: Array<{ uid: string; name: string; color: string }>;
  onMoreClick?: () => void;
  isModal?: boolean;
}> = ({
  environments = [],
  environmentsList = [],
  onMoreClick,
  isModal,
}) => {
  const sortedEnvironments = useMemo(() => {
    return sortEnvironmentsByTime(environments);
  }, [environments]);

  return (
    <div className="version-timeline__environments flex-v-center mb-8">
      <EnvironmentItem
        env={sortedEnvironments[0]}
        environmentsList={environmentsList}
        environments={sortedEnvironments}
        isModal={isModal}
      />
      {sortedEnvironments.length > 1 && (
        <div 
          className="version-timeline__more-environments ml-5"
          onClick={onMoreClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onMoreClick) {
              onMoreClick();
            }
          }}
          {...BUTTON_ATTRIBUTES}
          data-test-id="cs-version-timeline-more-environments"
        >
          <span className="text-link">+{sortedEnvironments.length - 1} more</span>
        </div>
      )}
    </div>
  );
};

export const EnvironmentsModalContent: React.FC<{
  environments: IVersionTimeline['environments'];
  environmentsList: Array<{ uid: string; name: string; color: string }>;
  closeModal: (data?: any) => void;
  version: IVersionTimeline['version'];
  versionName: string;
  locales?: Array<{ code: string; name: string }>;
}> = ({ 
  environments, 
  environmentsList,
  closeModal,
  version,
  versionName,
  locales = [],
}) => {
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const sortedEnvironments = useMemo(() => {
    return sortEnvironmentsByTime(environments);
  }, [environments]);

  const handleSearch = (value: string) => {
    if (value.length === 0) {
      setSearchValue('');
      setSearchResults([]);
      return;
    }

    const searchData = sortedEnvironments.filter(env => {
      const envName = getEnvName(env.environment, environmentsList);
      return envName.toLowerCase().includes(value.toLowerCase());
    });

    setSearchValue(value);
    setSearchResults(searchData);
  };

  const displayEnvironments = searchValue.length > 0 ? searchResults : sortedEnvironments;

  return (
    <>
      <ModalHeader 
        title={`${getLocaleName(version.locale, locales)} | ${versionName}`} 
        closeModal={closeModal} 
      />
      <ModalBody>
        {environments.length > 5 && (
          <Search 
            placeholder=" Search environments" 
            onChange={handleSearch} 
            value={searchValue} 
            width="large"
            type="secondary"
            data-test-id="cs-version-timeline-environments-modal-search"
            version="v2"
            className="cs-version-timeline-environments-modal-search"
          />
        )}
        <div>
          {searchValue.length > 0 && searchResults.length === 0 ? (
            <div className="version-timeline__empty-state">
              <EmptyState heading="No results found" displayImage={false} type="tertiary" />
            </div>
          ) : (
            displayEnvironments.map((env) => (
              <div key={env.environment} className="version-timeline__environment-container ml-8">
                <EnvironmentHeader
                  env={env}
                  environmentsList={environmentsList}
                  showPill={true}
                />
                <VersionTimelineDateTime dateTime={env.scheduled_at ? env.scheduled_at : env.time} iconType="publish"/>
                <VersionTimelineUser userName={env.user}/>
              </div>
            ))
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={closeModal}
          testId="cs-version-timeline-modal-cancel"
        >
           Close
        </Button>
      </ModalFooter>
    </>
  );
};

const LatestVersionPill = () => (
  <Pill 
    shouldHaveBorder={false} 
    isSortable={false} 
    size="small"
    variant="label" 
    status="default" 
    items={[{ id: 1, text: 'Latest' }]} 
    className="ml-5"
    stroke={'#007A52'}
    background={"#F5FFFC"}
    testId="cs-version-timeline-latest"
  />
);

const VersionTimeline: React.FunctionComponent<IVersionTimeline> = ({
  version,
  onVersionNameChange,
  versionOnClick,
  compareVersion,
  latestVersion,
  dateTime,
  userName,
  environments = [],
  environmentsList = [],
  defaultSelected,
  locales = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [versionName, setVersionName] = useState(getVersionDisplayName(version));
  const [hasShownNotification, setHasShownNotification] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const versionRef = useRef<HTMLDivElement>(null);

  const isLatestVersion = version._version === latestVersion._version;
  const isSelectedVersion = defaultSelected && version._version === defaultSelected._version;
  const showCompareButton = compareVersion && !isSelectedVersion;

  // Memoize the icon calculation
  const versionIcon = useMemo(() => {
    if (isLatestVersion) {
      return <Icon icon="VersionBadgeLatest" version="v2" size="mini"/>;
    }
    
    if (!environments || environments.length === 0) {
      return <Icon icon="VersionBadgeOther" version="v2" size="mini" />;
    }

    const isPublished = environments.some(env => 
      env && !env.scheduled_at
    );
    
    const isScheduled = environments.some(env => 
      env && env.scheduled_at
    );
    
    if (isPublished || isScheduled) {
      return <Icon icon="VersionBadgeScheduled" version="v2" size="mini"/>;
    }
    
    return <Icon icon="VersionBadgeOther" version="v2" size="mini" />;
  }, [isLatestVersion, environments]);

  const handleVersionNameChange = (e: React.ChangeEvent<HTMLInputElement>, setVersionName: (name: string) => void) => {
    const newValue = e.target.value;
    setVersionName(newValue);
    
    if (newValue.length > 32 && !hasShownNotification) {
      Notification({
        notificationContent: {text: "Version name cannot exceed 32 characters"},
        type: 'error',
        testId: 'cs-version-timeline-name-limit-notification'
      });
      setHasShownNotification(true);
    } else if (newValue.length <= 32) {
      setHasShownNotification(false);
    }
  };

  const handleSave = () => {
    const trimmedName = versionName.trim();
    if (onVersionNameChange && trimmedName && trimmedName.length <= 32) {
      onVersionNameChange(trimmedName);
    } else {
      // If empty, exceeds limit, or no onVersionNameChange, revert to original name
      setVersionName(version._version_name?.title || `Version ${version._version}`);
    }
    setIsEditing(false);
    setShowActionButtons(false);
  };

  const enableEditMode = () => {
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };
  
  const handleVersionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (versionOnClick) {
      versionOnClick(version._version);
    }
  };

  const handleFocus = () => {
    setShowActionButtons(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setShowActionButtons(false);
    }
  };

  const handleEditIconHover = (isHovering: boolean) => {
    if (versionRef.current) {
      if (isHovering) {
        versionRef.current.style.border = '2px solid #b6aef3';
      } else {
        versionRef.current.style.border = '';
      }
    }
  };

  const getCompareTooltipContent = () => {
    if (!defaultSelected) {
      // When no version is selected, show "Compare [current version] with [latest version]"
      return `Compare ${getVersionDisplayName(version)} with ${getVersionDisplayName(latestVersion)}`;
    }
    
    // When a version is selected, show "Compare [selected version] with [current version]"
    return `Compare ${getVersionDisplayName(defaultSelected)} with ${getVersionDisplayName(version)}`;
  };

  const renderEnvironmentInfo = () => {
    // Only render environment DOM if we have environments
    if (!environments || environments.length === 0) {
      return null;
    }

    const openEnvironmentsModal = () => {
      cbModal({
        component: (props) => (
          <EnvironmentsModalContent 
            environments={environments} 
            environmentsList={environmentsList}
            version={version}
            versionName={versionName}
            locales={locales}
            {...props} 
          />
        ),
        modalProps: {
          size: 'medium',
          shouldCloseOnOverlayClick: true,
          shouldCloseOnEscape: true
        },
        testId: 'cs-version-timeline-environments-modal'
      });
    };

    return (
      <VersionTimelineEnvironments 
        environments={environments} 
        environmentsList={environmentsList}
        onMoreClick={openEnvironmentsModal}
      />
    );
  };

  return (
    <div className="VersionTimeline" data-test-id="cs-version-timeline">
      <div className='VersionTimeline__container'>
        <div className="VersionTimeline__content flex">
          <div className='VersionTimeline__icon'>{versionIcon}</div>
          <div>
            <div className="VersionTimeline__name-section">
              {isEditing ? (
                <div className="VersionTimeline__edit-mode">
                  <TextInput
                    ref={inputRef}
                    data-test-id="cs-version-timeline-input"
                    placeholder="Enter version name"
                    type="text"
                    width="medium"
                    value={versionName}
                    onChange={(e) => handleVersionNameChange(e, setVersionName)}
                    onKeyDown={(e) => handleVersionNameKeyDown(e, handleSave, setIsEditing, versionName, setVersionName)}
                    onBlur={handleSave}
                    autoFocus={true}
                    maxLength={32}
                    version="v2"
                  />
                </div>
              ) : (
                <div className="VersionTimeline__view-mode" onFocus={handleFocus} onBlur={handleBlur}>
                  <div
                    ref={versionRef}
                    data-test-id="cs-version-timeline-version"
                    onClick={versionOnClick ? handleVersionClick : undefined}
                    onKeyDown={(e) => handleVersionClickKeyDown(e, versionOnClick, handleVersionClick)}
                    {...BUTTON_ATTRIBUTES}
                    className={`VersionTimeline__version flex-v-center`}>
                    <span>
                      <Truncate 
                        isResponsive 
                        truncateFrom="end"
                        testId="cs-version-timeline-version-name"
                      >
                        {versionName}
                      </Truncate>
                    </span>
                    {isLatestVersion && <LatestVersionPill />}
                  </div>
                  <div className={`VersionTimeline__action-buttons ${showActionButtons ? 'VersionTimeline__action-buttons--show' : ''}`}>
                    <Tooltip content="Rename" position="bottom" showArrow={false}>
                      <div
                        data-test-id="cs-version-timeline-edit"
                        onClick={enableEditMode}
                        onKeyDown={(e) => handleEditModeKeyDown(e, enableEditMode)}
                        onMouseEnter={() => handleEditIconHover(true)}
                        onMouseLeave={() => handleEditIconHover(false)}
                        {...BUTTON_ATTRIBUTES}
                        aria-label="Rename version"
                        className="VersionTimeline__action-button"
                      >
                        <Icon icon="Edit" version='v2' size="small" />
                      </div>
                    </Tooltip>
                    {showCompareButton && (
                      <Tooltip content={getCompareTooltipContent()} position="bottom" showArrow={false}>
                        <div
                          data-test-id="cs-version-timeline-compare"
                          onClick={(e) => handleCompareClick(e as MouseEvent, compareVersion, version, defaultSelected)}
                          onKeyDown={(e) => handleCompareKeyDown(e, (e) => handleCompareClick(e as MouseEvent, compareVersion, version, defaultSelected))}
                          {...BUTTON_ATTRIBUTES}
                          aria-label={getCompareTooltipContent()}
                          className="VersionTimeline__action-button"
                        >
                          <Icon icon="Compare" version='v2' size="small" />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="VersionTimeline__info">
              <VersionTimelineDateTime dateTime={dateTime} />
              <VersionTimelineUser userName={userName}/>
              {renderEnvironmentInfo()}
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionTimeline;